/**
 * Client-side draft for an in-progress quiz attempt.
 * Survives remounts, tab switches, and full page reloads until submit or Start over.
 */

import type { OptionOrder } from '@/lib/quiz-option-shuffle';

export interface QuizInProgressSession {
  /** v2 adds optionOrders for answer-letter shuffle. */
  v: 1 | 2;
  slug: string;
  documentId: string;
  questionIds: string[];
  /** Display-letter answers (a-d as shown); convert to canonical on submit. */
  answers: Record<string, string>;
  /** Per questionId: permutation of canonical keys in display slots a-d. */
  optionOrders?: Record<string, OptionOrder>;
  index: number;
  startedAt: string;
  endsAt: string;
  updatedAt: string;
}

function storageKey(slug: string, documentId: string): string {
  return `maater:quiz-progress:v1:${slug}:${documentId}`;
}

export function orderQuestionsByIds<T extends { id: string }>(
  questions: T[],
  ids: string[]
): T[] | null {
  if (ids.length === 0 || questions.length === 0) return null;
  const byId = new Map(questions.map((q) => [q.id, q]));
  if (ids.some((id) => !byId.has(id))) return null;
  if (ids.length !== questions.length) return null;
  const idSet = new Set(ids);
  if (questions.some((q) => !idSet.has(q.id))) return null;
  return ids.map((id) => byId.get(id)!);
}

export function loadQuizInProgressSession(
  slug: string,
  documentId: string
): QuizInProgressSession | null {
  if (!slug || !documentId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(slug, documentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QuizInProgressSession;
    if (
      (parsed?.v !== 1 && parsed?.v !== 2) ||
      parsed.slug !== slug ||
      parsed.documentId !== documentId
    ) {
      return null;
    }
    if (!Array.isArray(parsed.questionIds) || typeof parsed.answers !== 'object') {
      return null;
    }
    if (!parsed.startedAt || !parsed.endsAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveQuizInProgressSession(session: QuizInProgressSession): void {
  if (!session.slug || !session.documentId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      storageKey(session.slug, session.documentId),
      JSON.stringify({ ...session, updatedAt: new Date().toISOString() })
    );
  } catch {
    // Quota / private mode - ignore
  }
}

export function clearQuizInProgressSession(slug: string, documentId: string): void {
  if (!slug || !documentId || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(slug, documentId));
  } catch {
    // ignore
  }
}

/**
 * Empty ActiveMode seeds (0 answers, still on Q1) are not real attempts.
 * Showing "Resume" on first open confused learners who never started.
 */
export function isResumableQuizDraft(draft: QuizInProgressSession | null | undefined): boolean {
  if (!draft) return false;
  if (Object.keys(draft.answers || {}).length > 0) return true;
  if ((draft.index ?? 0) > 0) return true;
  return false;
}
